import { useState, useEffect } from "react";
import type { CafeLogInput } from "../types";
import { PROCESS_OPTIONS, BREW_METHOD_OPTIONS, KIND_OPTIONS, isTea, kindLabel } from "../types";

interface Props {
  initial?: Partial<CafeLogInput>;
  submitLabel?: string;
  onSubmit: (input: CafeLogInput) => Promise<void> | void;
  onCancel?: () => void;
  /** Fires on every edit so the parent can merge AI-filled fields into live state. */
  onChange?: (current: CafeLogInput) => void;
  /** Photos + AI block, rendered at the top of the form by the parent page. */
  children?: React.ReactNode;
}

const empty: CafeLogInput = {
  cafe_name: "", cafe_city: null, visited_on: todayIso(),
  kind: "coffee", variety: null, process: null, country: null, region: null, roaster: null,
  method: null, dose_g: null, ratio: null, temp_c: null, brew_time_s: null, grind_size: null,
  pours: null, score: null, taste_notes: null, flavor_tags: [],
};

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

// Render stored seconds back as "m:ss" for the input's initial value.
function formatBrew(totalSec: number | null | undefined): string {
  if (totalSec == null) return "";
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, "0")}` : `${s}`;
}

// Accept flexible brew-time formats and normalise to total seconds:
//   "2:30" / "2.30" → 150   "2m30" / "2m30s" / "2m" → 150 / 120
//   "90s" → 90   "150" (plain) → 150 seconds
function parseBrew(raw: string): number | null {
  const t = raw.trim().toLowerCase();
  if (!t) return null;
  // min/sec separated by : or .  (a dot here means "minutes:seconds", not a decimal)
  let m = t.match(/^(\d+)\s*[:.]\s*(\d{1,2})$/);
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  // "2m30", "2m30s", "2m"
  m = t.match(/^(\d+)\s*m(?:\s*(\d{1,2})\s*s?)?$/);
  if (m) return Number(m[1]) * 60 + (m[2] ? Number(m[2]) : 0);
  // "90s" → seconds
  m = t.match(/^(\d+)\s*s$/);
  if (m) return Number(m[1]);
  // bare number → seconds
  m = t.match(/^\d+$/);
  if (m) return Number(t);
  return null;
}

// Mirror CoffeeForm: AI can return null for text fields. Coerce the one required
// field (cafe_name) to a string so .trim() never throws and silently kills the submit.
function sanitize(initial?: Partial<CafeLogInput>): CafeLogInput {
  const merged = { ...empty, ...(initial || {}) };
  if (merged.cafe_name == null) merged.cafe_name = "";
  return merged;
}

export function CafeLogForm({ initial, submitLabel = "Save", onSubmit, onCancel, onChange, children }: Props) {
  const [v, setV] = useState<CafeLogInput>(sanitize(initial));
  const [tagsText, setTagsText] = useState((initial?.flavor_tags || []).join(", "));
  // Brew time is stored as total seconds but entered free-form (2:30, 2.30, 2m30, 150…).
  const [brewText, setBrewText] = useState(() => formatBrew(initial?.brew_time_s));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoErr, setGeoErr] = useState<string | null>(null);

  useEffect(() => {
    if (initial) {
      setV(sanitize(initial));
      setTagsText((initial.flavor_tags || []).join(", "));
      setBrewText(formatBrew(initial.brew_time_s));
    }
  }, [initial]);

  // Parse the free-form brew-time text to total seconds on every keystroke.
  const onBrewText = (t: string) => {
    setBrewText(t);
    set("brew_time_s", parseBrew(t));
  };

  const fetchCity = () => {
    if (!("geolocation" in navigator)) { setGeoErr("Location not supported on this device."); return; }
    setGeoBusy(true); setGeoErr(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const r = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
          const j = await r.json() as { city?: string; locality?: string; principalSubdivision?: string };
          const city = j.city || j.locality || j.principalSubdivision || "";
          if (city) set("cafe_city", city);
          else setGeoErr("Couldn't determine the city from your location.");
        } catch {
          setGeoErr("Location lookup failed — type the city instead.");
        } finally { setGeoBusy(false); }
      },
      (e) => { setGeoBusy(false); setGeoErr(e.code === e.PERMISSION_DENIED ? "Location permission denied." : "Location unavailable."); },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 },
    );
  };

  // Surface live state (incl. parsed flavor_tags) so the parent can merge AI fields
  // without clobbering edits the user already typed.
  useEffect(() => {
    if (!onChange) return;
    const flavor_tags = tagsText.split(",").map((s) => s.trim()).filter(Boolean);
    onChange({ ...v, flavor_tags });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v, tagsText]);

  const tea = isTea(v.kind);
  const set = <K extends keyof CafeLogInput>(k: K, val: CafeLogInput[K]) => setV((s) => ({ ...s, [k]: val }));
  const setNum = (k: keyof CafeLogInput, raw: string) =>
    setV((s) => ({ ...s, [k]: raw === "" ? null : Number(raw) }));

  const reportError = (msg: string) => {
    setError(msg);
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!String(v.cafe_name ?? "").trim()) {
      reportError("Café name is required. Fill it in and tap save again.");
      return;
    }
    setBusy(true);
    try {
      const flavor_tags = tagsText.split(",").map((s) => s.trim()).filter(Boolean);
      await onSubmit({ ...v, flavor_tags });
    } catch (e) {
      reportError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6">
      {error ? (
        <div className="border-2 border-red-500 bg-red-50 text-red-800 px-3 py-2 text-sm font-medium">
          {error}
        </div>
      ) : null}

      {children}

      <Section title="Café">
        <Field label="Café name *"><input className="input" value={v.cafe_name} onChange={(e) => set("cafe_name", e.target.value)} placeholder="e.g. Coffee Collective" /></Field>
        <Field label="City">
          <div className="flex gap-2">
            <input className="input flex-1" value={v.cafe_city ?? ""} onChange={(e) => set("cafe_city", e.target.value || null)} placeholder="e.g. Copenhagen" />
            <button type="button" className="btn-ghost shrink-0" onClick={fetchCity} disabled={geoBusy} title="Use my current location">
              {geoBusy ? "…" : "📍"}
            </button>
          </div>
          {geoErr ? <p className="text-xs text-red-700 mt-1">{geoErr}</p> : null}
        </Field>
        <Field label="Date"><input type="date" className="input" value={v.visited_on ?? ""} onChange={(e) => set("visited_on", e.target.value || null)} /></Field>
      </Section>

      <Section title="Bean">
        <Field label="Kind" full>
          <div className="flex flex-wrap gap-2">
            {KIND_OPTIONS.map((k) => (
              <button type="button" key={k} onClick={() => set("kind", k)}
                className={`px-3 py-1 text-xs uppercase tracking-widest border ${v.kind === k ? "bg-ink text-paper-50 border-ink" : "bg-paper-50 text-paper-600 border-paper-300 hover:border-ink hover:text-ink"}`}>
                {kindLabel(k)}
              </button>
            ))}
          </div>
        </Field>
        <Field label={tea ? "Cultivar" : "Variety"}><input className="input" value={v.variety ?? ""} onChange={(e) => set("variety", e.target.value || null)} placeholder="e.g. Geisha" /></Field>
        {tea ? null : (
          <Field label="Process">
            <input className="input" list="cafe-process-suggestions" value={v.process ?? ""} onChange={(e) => set("process", e.target.value || null)} placeholder="Washed, Natural, Anaerobic…" />
            <datalist id="cafe-process-suggestions">
              {PROCESS_OPTIONS.map((p) => <option key={p} value={p} />)}
            </datalist>
          </Field>
        )}
        <Field label="Country"><input className="input" value={v.country ?? ""} onChange={(e) => set("country", e.target.value || null)} /></Field>
        <Field label="Region"><input className="input" value={v.region ?? ""} onChange={(e) => set("region", e.target.value || null)} /></Field>
        <Field label={tea ? "Producer" : "Roaster"}><input className="input" value={v.roaster ?? ""} onChange={(e) => set("roaster", e.target.value || null)} /></Field>
        <Field label="Flavor tags (comma separated)" full>
          <input className="input" value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="Jasmine, Mango, Bergamot" />
        </Field>
      </Section>

      <Section title="Brew recipe">
        <Field label="Method">
          <input className="input" list="cafe-method-suggestions" value={v.method ?? ""} onChange={(e) => set("method", e.target.value || null)} placeholder="V60 / Espresso / Aeropress…" />
          <datalist id="cafe-method-suggestions">
            {BREW_METHOD_OPTIONS.map((m) => <option key={m} value={m} />)}
          </datalist>
        </Field>
        <Field label="Dose (g)"><input type="number" step="0.1" inputMode="decimal" className="input" value={v.dose_g ?? ""} onChange={(e) => setNum("dose_g", e.target.value)} /></Field>
        <Field label="Ratio / yield"><input className="input" value={v.ratio ?? ""} onChange={(e) => set("ratio", e.target.value || null)} placeholder="1:16 or 40g out" /></Field>
        <Field label="Water temp (°C)"><input type="number" inputMode="numeric" className="input" value={v.temp_c ?? ""} onChange={(e) => setNum("temp_c", e.target.value)} /></Field>
        <Field label="Brew time">
          <input className="input" value={brewText} onChange={(e) => onBrewText(e.target.value)}
            placeholder="2:30  ·  2.30  ·  2m30  ·  150" />
          {v.brew_time_s != null ? (
            <p className="text-xs text-paper-500 mt-1">= {formatBrew(v.brew_time_s)} ({v.brew_time_s}s)</p>
          ) : brewText.trim() ? (
            <p className="text-xs text-red-700 mt-1">Couldn't read that — try 2:30, 2.30, 2m30, or 150</p>
          ) : null}
        </Field>
        <Field label="Grind size"><input className="input" value={v.grind_size ?? ""} onChange={(e) => set("grind_size", e.target.value || null)} placeholder="grinder-relative, e.g. EK 8.0" /></Field>
        <Field label="Pours / pulses"><input type="number" inputMode="numeric" className="input" value={v.pours ?? ""} onChange={(e) => setNum("pours", e.target.value)} /></Field>
      </Section>

      <Section title="Review">
        <Field label="Score (0–100)"><input type="number" step="0.25" inputMode="decimal" className="input" value={v.score ?? ""} onChange={(e) => setNum("score", e.target.value)} placeholder="e.g. 90" /></Field>
        <Field label="Taste notes" full>
          <textarea className="input min-h-[120px]" value={v.taste_notes ?? ""} onChange={(e) => set("taste_notes", e.target.value || null)} placeholder="How did it taste? Impression, what stood out…" />
        </Field>
      </Section>

      <div className="flex gap-2 justify-end">
        {onCancel ? <button type="button" className="btn-ghost" onClick={onCancel}>Cancel</button> : null}
        {/* type=button + manual onClick is more reliable on iOS than the implicit submit. */}
        <button type="button" className="btn-primary" disabled={busy} onClick={(e) => submit(e as unknown as React.FormEvent)}>
          {busy ? "Saving…" : submitLabel}
        </button>
      </div>
    </form>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <div className="eyebrow border-b border-paper-200 pb-1">{title}</div>
      <div className="grid gap-4 md:grid-cols-2">{children}</div>
    </div>
  );
}

function Field({ label, full, children }: { label: string; full?: boolean; children: React.ReactNode }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}
